import { auth, getFirebaseApp } from '@/constants/firebaseConfig'
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  signInWithCustomToken,
  signInWithEmailAndPassword
} from 'firebase/auth'

export const signInWithEmailPassword = async (
  email: string,
  password: string
) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    )
    return userCredential.user
  } catch (error) {
    console.error('Failed to login user with email and password', error)
    throw error
  }
}

// Function to authenticate using a custom token
export const signInWithToken = async (token: string) => {
  try {
    const authInstance = getAuth(getFirebaseApp())
    const userCredential = await signInWithCustomToken(authInstance, token)

    return userCredential.user
  } catch (error) {
    console.error('Failed to sign in with custom token', error)
    throw error
  }
}

export const getAuthTokenFromCookie = () => {
  const match = document.cookie.match(/(^| )session_token=([^;]+)/)
  return match ? match[2] : null
}

export const getFirebaseAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser

  if (user) {
    return await user.getIdToken()
  }
  return null
}

// Register user with email and password
export const createUserAuthRecord = async (user: {
  email: string
  password: string
}) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      user.email,
      user.password
    )
    //Sending email verification
    await sendEmailVerification(userCredential.user)
    return userCredential.user
  } catch (error) {
    console.error('Failed to register user with email and password', error)
    throw error
  }
}

// Converts Firebase email verification error codes into user-friendly messages
export const parseFirebaseErrorCode = (code: string): string => {
  switch (code) {
    case 'auth/expired-action-code':
      return 'This verification link has expired. Please request a new one from your account settings or try signing in again.'

    case 'auth/invalid-action-code':
      return 'This verification link is invalid or has already been used. Please check your email for a newer link or try again.'

    case 'auth/user-disabled':
      return 'Your account has been disabled. Please contact support for further assistance.'

    case 'auth/argument-error':
      return 'The verification link is incomplete or malformed. Please check your email and try again.'

    case 'auth/internal-error':
      return 'An internal error occurred while verifying your email. Please try again later.'

    case 'auth/invalid-email':
      return 'The email address is not valid. Please check and try again.'

    case 'auth/user-not-found':
      return 'No user found with this email address. Please make sure you’re using the correct email.'

    case 'auth/missing-email':
      return 'Please provide an email address to send the reset password link.'

    case 'auth/too-many-requests':
      return 'Too many requests have been made. Please try again later.'
    default:
      return 'An unknown error occurred. Please try again later or contact support for further assistance.'
  }
}
